import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, RefreshCw, TrendingUp, Calendar } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PredictionsGrid() {
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);

  const fetchPredictions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/predictions/latest`);
      
      // Get only the latest prediction for each stock (horizon=2)
      const latestByStock = {};
      (res.data.predictions || []).forEach(pred => {
        if (pred.horizon === 2) {
          if (!latestByStock[pred.symbol] || 
              new Date(pred.prediction_date) > new Date(latestByStock[pred.symbol].prediction_date)) {
            latestByStock[pred.symbol] = pred;
          }
        }
      });
      
      setPredictions(Object.values(latestByStock));
      toast.success(`Loaded ${Object.keys(latestByStock).length} predictions`);
    } catch (err) {
      toast.error("Failed to fetch predictions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPredictions();
  }, []);

  const getFutureDate = (predictionDate) => {
    const pred = new Date(predictionDate);
    const future = new Date(pred);
    future.setDate(future.getDate() + 2);
    return future.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getCurrentDate = (predictionDate) => {
    const pred = new Date(predictionDate);
    return pred.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <TrendingUp className="h-8 w-8" />
              Price Predictions (2 Days)
            </h1>
            <p className="text-gray-400 mt-1">
              Current prices and predicted prices for all 20 stocks
            </p>
          </div>
          <Button onClick={fetchPredictions} disabled={loading} size="lg">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {predictions.length === 0 && !loading ? (
          <Card className="p-8 text-center">
            <Calendar className="h-12 w-12 mx-auto text-gray-500 mb-3" />
            <h3 className="text-xl font-semibold mb-2">No Predictions Available</h3>
            <p className="text-gray-400 mb-4">
              Predictions are generated daily at 4:00 PM IST
            </p>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="predictions-table">
              <thead>
                <tr className="border-b-2 border-gray-700">
                  <th className="text-left p-4 font-semibold">Stock</th>
                  <th className="text-left p-4 font-semibold">Sector</th>
                  <th className="text-right p-4 font-semibold">Current Price</th>
                  <th className="text-center p-4 font-semibold">Date</th>
                  <th className="text-right p-4 font-semibold">Predicted Price</th>
                  <th className="text-center p-4 font-semibold">Expected Date</th>
                  <th className="text-right p-4 font-semibold">Actual Price</th>
                  <th className="text-center p-4 font-semibold">Direction</th>
                  <th className="text-center p-4 font-semibold">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {predictions.map((pred, idx) => {
                  const isEvaluated = pred.evaluated;
                  const actualPrice = pred.actual_price;
                  
                  return (
                    <tr 
                      key={idx} 
                      className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                      data-testid="prediction-row"
                    >
                      <td className="p-4">
                        <div className="font-bold text-lg">{pred.symbol.replace(".NS", "")}</div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="text-xs">
                          {pred.symbol === "RELIANCE.NS" ? "Energy" :
                           pred.symbol === "TCS.NS" || pred.symbol === "INFY.NS" || pred.symbol === "TECHM.NS" || pred.symbol === "WIPRO.NS" ? "IT" :
                           pred.symbol === "HDFCBANK.NS" || pred.symbol === "ICICIBANK.NS" || pred.symbol === "SBIN.NS" || pred.symbol === "KOTAKBANK.NS" || pred.symbol === "AXISBANK.NS" ? "Banking" :
                           pred.symbol === "ITC.NS" || pred.symbol === "HINDUNILVR.NS" ? "FMCG" :
                           pred.symbol === "SUNPHARMA.NS" ? "Pharma" :
                           pred.symbol === "MARUTI.NS" ? "Auto" :
                           pred.symbol === "BAJFINANCE.NS" ? "NBFC" :
                           pred.symbol === "ASIANPAINT.NS" ? "Paint" :
                           pred.symbol === "LT.NS" ? "Infra" :
                           pred.symbol === "BHARTIARTL.NS" ? "Telecom" :
                           pred.symbol === "NTPC.NS" ? "Power" :
                           pred.symbol === "TATASTEEL.NS" ? "Metals" : "Other"}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="text-lg font-semibold">₹{pred.current_price}</div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="text-sm text-gray-400">
                          {getCurrentDate(pred.prediction_date)}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="text-lg font-bold text-blue-400">
                          ₹{pred.predicted_price}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-400" />
                          <span className="text-sm font-semibold text-blue-400">
                            {getFutureDate(pred.prediction_date)}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        {isEvaluated && actualPrice ? (
                          <div className="text-lg font-bold text-green-400">
                            ₹{actualPrice.toFixed(2)}
                          </div>
                        ) : (
                          <div className="text-gray-600 italic">
                            Pending
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <Badge 
                          className={
                            pred.direction === "Bullish" ? "bg-green-900/50 text-green-300 border-green-700" :
                            pred.direction === "Bearish" ? "bg-red-900/50 text-red-300 border-red-700" :
                            "bg-gray-800 text-gray-300 border-gray-700"
                          }
                        >
                          {pred.direction}
                        </Badge>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 bg-gray-800 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${pred.confidence}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold">{pred.confidence}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-center text-xs text-gray-500">
          <p>Predictions generated daily at 4:00 PM IST • Actual prices will be updated after 2 days</p>
          <p className="mt-1">Refresh this page to see the latest predictions</p>
        </div>
      </div>
    </div>
  );
}
